/**
 * CameraViewPage - Visualização de Câmeras de um Dispositivo
 *
 * Exibe grid 2x2 com os 4 canais de câmera do dispositivo.
 * Rota: /camera/:deviceId
 */

import {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Snackbar,
  Button,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SdStorageIcon from '@mui/icons-material/SdStorage';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TimerIcon from '@mui/icons-material/Timer';

import JessibucaPlayer from '../components/JessibucaPlayer';
import { useDeviceCameraStatus } from '../hooks/useCameraStatus';
import { useVideoCommand } from '../hooks/useVideoCommand';
import { useCameraCapabilities } from '../hooks/useCameraCapabilities';
import { CHANNEL_NAMES, STREAM_DURATION_OPTIONS } from '../utils/cameraConstants';
import useStreamTimer from '../hooks/useStreamTimer';
import useVisibilityPause from '../hooks/useVisibilityPause';
import videoPlaceholder from '../../resources/images/custom/video.png';

// ============================================
// Styled Components
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: 0,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
  },
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  overflow: 'auto',
  minHeight: 0,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
}));

const CameraGridStyled = styled(Box, {
  shouldForwardProp: (prop) => !['layout', 'isMobile'].includes(prop),
})(({ theme, layout, isMobile }) => ({
  ...(isMobile ? {} : { flex: 1 }),
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: isMobile ? '1fr' : (layout === 'single' ? '1fr' : 'repeat(2, 1fr)'),
  gridTemplateRows: isMobile ? 'auto' : (layout === 'single' ? '1fr' : layout === 'dual' ? '1fr' : 'repeat(2, 1fr)'),
  minHeight: 0,
}));

const CameraCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  position: 'relative',
  backgroundColor: '#000',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `2px solid ${isActive ? theme.palette.success.main : theme.palette.divider}`,
  transition: 'border-color 0.2s ease',
  minHeight: 200,
  [theme.breakpoints.down('md')]: {
    aspectRatio: '16/9',
    minHeight: 'unset',
  },
  '&:hover': {
    borderColor: isActive ? theme.palette.success.main : theme.palette.action.hover,
  },
}));

const CameraOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1),
  background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 10,
}));

const ChannelBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive, theme }) => ({
  backgroundColor: isActive ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.text.primary, 0.1),
  color: isActive ? theme.palette.success.main : theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: '11px',
  height: 24,
  '& .MuiChip-icon': {
    color: isActive ? theme.palette.success.main : theme.palette.text.disabled,
  },
}));

// ============================================
// Layout Options
// ============================================
const LAYOUTS = {
  single: { label: '1 câmera', icon: <GridViewIcon fontSize="small" />, channels: 1 },
  dual: { label: '2 câmeras', icon: <ViewStreamIcon fontSize="small" />, channels: 2 },
  quad: { label: '4 câmeras', icon: <ViewModuleIcon fontSize="small" />, channels: 4 },
};

// ============================================
// Main Component
// ============================================
const CameraViewPage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const device = useSelector((state) => state.devices.items[deviceId]);

  // Camera status from M7S (use IMEI, not deviceId)
  const { hasCamera: hasActiveStream, activeChannels, refresh, isLoading } = useDeviceCameraStatus(device?.uniqueId);

  // Video command hook
  const { requestVideo, stopVideo } = useVideoCommand();

  // Camera capabilities from device (queries 0x9003 automatically)
  const {
    channelCount,
    isReady: capabilitiesReady,
    hasCapabilities,
  } = useCameraCapabilities(deviceId, { autoQuery: true });

  // Local state
  const [layout, setLayout] = useState('quad');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(1);

  // Per-channel streaming state (channels the user has started)
  const [activeStreaming, setActiveStreaming] = useState(new Set());
  const activeStreamingRef = useRef(new Set());

  // Snackbar state for stream timer
  const [snackbar, setSnackbar] = useState({ open: false, message: '', warningChannel: null });

  // Stream timer refs to avoid stale closures in callbacks
  const handleStopChannelRef = useRef(null);

  // Stream duration timer
  const {
    startTimer, stopTimer, extendTimer, getRemainingTime,
    durationMinutes, setDurationMinutes,
  } = useStreamTimer({
    onTimeout: (ch) => {
      if (handleStopChannelRef.current) handleStopChannelRef.current(ch);
      // Check if more channels are still active (may expire soon)
      const remaining = activeStreamingRef.current.size;
      setSnackbar({
        open: true,
        message: remaining > 0
          ? `Stream CH${ch} encerrado (${remaining} canal${remaining > 1 ? 'ais' : ''} restante${remaining > 1 ? 's' : ''})`
          : `Streams encerrados por limite de tempo`,
        warningChannel: null,
      });
    },
    onWarning: () => {
      // Show warning for ALL active channels, not just the first one
      setSnackbar({ open: true, message: 'Streams serão encerrados em ~1 minuto', warningChannel: 'all' });
    },
  });

  // Pause streams when tab is hidden (save M2M data)
  useVisibilityPause({
    onHidden: (channels) => {
      channels.forEach((ch) => {
        stopVideo(deviceId, { channel: ch }).catch(() => {});
        stopTimer(ch);
      });
      setActiveStreaming(new Set());
    },
    onVisible: (channels) => {
      channels.forEach((ch) => {
        requestVideo(deviceId, { channel: ch }).catch(() => {});
        startTimer(ch);
      });
      setActiveStreaming(new Set(channels));
    },
    getActiveChannels: () => activeStreamingRef.current,
  });

  // Channels to display based on layout and detected capabilities
  const displayChannels = useMemo(() => {
    // Only show channels that actually exist on the device
    const maxAvailable = capabilitiesReady ? Math.min(channelCount, 8) : 0;

    // Don't render any players until capabilities are ready
    if (!capabilitiesReady || maxAvailable === 0) {
      return [];
    }

    const layoutChannels = LAYOUTS[layout]?.channels || 4;

    if (layoutChannels === 1) {
      return [selectedChannel];
    }
    if (layoutChannels === 2) {
      return Array.from({ length: Math.min(2, maxAvailable) }, (_, i) => i + 1);
    }
    // For quad layout, show only available channels (max 4)
    return Array.from({ length: Math.min(layoutChannels, maxAvailable) }, (_, i) => i + 1);
  }, [layout, selectedChannel, channelCount, capabilitiesReady]);

  // Keep ref in sync for cleanup/beforeunload
  useEffect(() => {
    activeStreamingRef.current = activeStreaming;
  }, [activeStreaming]);

  // Cleanup: stop all active streams on unmount
  useEffect(() => {
    return () => {
      activeStreamingRef.current.forEach((ch) => {
        stopVideo(deviceId, { channel: ch }).catch((err) => console.warn('[CameraView] cleanup stop failed:', err));
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // beforeunload: send stopVideo via sendBeacon when browser tab closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!deviceId) return;
      activeStreamingRef.current.forEach((ch) => {
        navigator.sendBeacon(
          '/api/commands/send',
          new Blob([JSON.stringify({
            deviceId: Number(deviceId),
            type: 'stopVideo',
            attributes: { channel: ch, streamType: 1 },
          })], { type: 'application/json' }),
        );
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [deviceId]);

  // Start a single channel
  const handleStartChannel = useCallback((channel) => {
    requestVideo(deviceId, { channel }).catch((err) => console.warn('[CameraView] requestVideo failed:', err));
    setActiveStreaming((prev) => new Set([...prev, channel]));
    startTimer(channel);
  }, [deviceId, requestVideo, startTimer]);

  // Stop a single channel
  const handleStopChannel = useCallback((channel) => {
    stopVideo(deviceId, { channel }).catch((err) => console.warn('[CameraView] stopVideo failed:', err));
    setActiveStreaming((prev) => {
      const next = new Set(prev);
      next.delete(channel);
      return next;
    });
    stopTimer(channel);
  }, [deviceId, stopVideo, stopTimer]);

  // Keep ref in sync for timer onTimeout callback
  handleStopChannelRef.current = handleStopChannel;

  // Stop all cameras
  const handleStopAll = useCallback(() => {
    activeStreaming.forEach((ch) => {
      stopVideo(deviceId, { channel: ch }).catch((err) => console.warn('[CameraView] stopAll failed:', err));
      stopTimer(ch);
    });
    setActiveStreaming(new Set());
  }, [deviceId, activeStreaming, stopVideo, stopTimer]);

  // Start all displayed channels
  const handleStartAll = useCallback(() => {
    const channels = displayChannels.length > 0
      ? displayChannels
      : Array.from({ length: Math.min(channelCount, 8) }, (_, i) => i + 1);
    Promise.allSettled(channels.map((ch) => requestVideo(deviceId, { channel: ch })));
    setActiveStreaming(new Set(channels));
    channels.forEach((ch) => startTimer(ch));
  }, [deviceId, displayChannels, channelCount, requestVideo, startTimer]);

  // Toggle fullscreen
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Handle playback navigation
  const handlePlayback = useCallback(() => {
    navigate(`/camera/${deviceId}/playback`);
  }, [navigate, deviceId]);

  // Handle settings navigation
  const handleSettings = useCallback(() => {
    navigate(`/camera/settings/${deviceId}`);
  }, [navigate, deviceId]);

  // If device not found
  if (!device) {
    return (
      <PageContainer>
        <Header>
          <IconButton onClick={handleBack} sx={{ color: 'inherit' }}>
            <ArrowBackIcon />
          </IconButton>
        </Header>
        <ContentArea>
          <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
            Dispositivo não encontrado
          </Alert>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <Header elevation={0}>
        <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <IconButton onClick={handleBack} sx={{ color: 'inherit' }}>
            <ArrowBackIcon />
          </IconButton>

          {!isMobile && <DirectionsCarIcon sx={{ color: tokens?.text?.tertiary }} />}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {device.name}
            </Typography>
            {!isMobile && (
              <Typography variant="caption" sx={{ color: tokens?.text?.tertiary }}>
                {device.uniqueId}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.5}>
            {hasActiveStream ? (
              <Chip
                icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
                label={isMobile ? 'LIVE' : 'AO VIVO'}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                  color: theme.palette.success.main,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: theme.palette.success.main },
                }}
              />
            ) : (
              <Chip
                label="OFFLINE"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.error.main, 0.2),
                  color: theme.palette.error.main,
                }}
              />
            )}
            {!isMobile && (
              <Chip
                label={!capabilitiesReady ? 'Consultando...' : `${channelCount} canais`}
                size="small"
                variant="outlined"
                color={hasCapabilities ? 'success' : 'default'}
                sx={{ borderColor: hasCapabilities ? undefined : theme.palette.divider, color: hasCapabilities ? undefined : tokens?.text?.secondary }}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Layout Toggle - desktop only */}
          {!isMobile && (
            <ToggleButtonGroup
              value={layout}
              exclusive
              onChange={(e, v) => v && setLayout(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: tokens?.text?.tertiary,
                  borderColor: theme.palette.divider,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.1),
                    color: theme.palette.text.primary,
                  },
                },
              }}
            >
              {Object.entries(LAYOUTS).map(([key, { icon }]) => (
                <ToggleButton key={key} value={key}>
                  <Tooltip title={LAYOUTS[key].label}>{icon}</Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {/* Stream duration selector */}
          {!isMobile && (
            <Tooltip title="Limite de tempo do stream">
              <Select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                size="small"
                variant="outlined"
                startAdornment={<TimerIcon sx={{ fontSize: 16, mr: 0.5, color: tokens?.text?.tertiary }} />}
                sx={{
                  minWidth: 95,
                  height: 32,
                  fontSize: '0.8rem',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                  '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center' },
                }}
              >
                {STREAM_DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Tooltip>
          )}

          <Tooltip title="Gravações">
            <IconButton onClick={handlePlayback} sx={{ color: tokens?.semantic?.warning }}>
              <SdStorageIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={refresh} disabled={isLoading} sx={{ color: 'inherit' }}>
                {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={activeStreaming.size > 0 ? 'Parar câmeras' : 'Iniciar todas'}>
            <IconButton
              onClick={activeStreaming.size > 0 ? handleStopAll : handleStartAll}
              sx={{ color: activeStreaming.size > 0 ? theme.palette.error.main : theme.palette.success.main }}
            >
              {activeStreaming.size > 0 ? <StopIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>

          {!isMobile && (
            <Tooltip title="Configurações">
              <IconButton onClick={handleSettings} sx={{ color: 'inherit' }}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={isFullscreen ? 'Sair tela cheia' : 'Tela cheia'}>
            <IconButton onClick={handleFullscreen} sx={{ color: 'inherit' }}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Header>

      {/* Camera Grid */}
      <ContentArea>
        {/* Loading state while querying capabilities */}
        {!capabilitiesReady && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
            }}
          >
            <CircularProgress color="inherit" />
            <Typography variant="body2" sx={{ color: tokens?.text?.secondary }}>
              Consultando câmeras do dispositivo...
            </Typography>
          </Box>
        )}

        {/* Camera grid when ready */}
        {capabilitiesReady && displayChannels.length > 0 && (
          <CameraGridStyled layout={isMobile ? 'single' : layout} isMobile={isMobile}>
            {displayChannels.map((channel) => {
              const isStreaming = activeStreaming.has(channel);
              const isActive = activeChannels.includes(channel);
              const channelName = CHANNEL_NAMES[channel] || `Canal ${channel}`;

              // Active player - streaming
              if (isStreaming) {
                return (
                  <CameraCell key={channel} isActive={isActive}>
                    <CameraOverlay>
                      <ChannelBadge
                        icon={<VideocamIcon />}
                        label={channelName}
                        isActive={isActive}
                        size="small"
                      />
                    </CameraOverlay>
                    <JessibucaPlayer
                      deviceImei={device.uniqueId}
                      channel={channel}
                      autoplay
                      remainingSeconds={getRemainingTime(channel)}
                      onStop={() => handleStopChannel(channel)}
                      onPause={() => stopVideo(deviceId, { channel }).catch((err) => console.warn('[CameraView] pause stop failed:', err))}
                      onResume={() => requestVideo(deviceId, { channel }).catch((err) => console.warn('[CameraView] resume request failed:', err))}
                    />
                  </CameraCell>
                );
              }

              // Idle placeholder - click to start
              return (
                <CameraCell
                  key={channel}
                  isActive={false}
                  onClick={() => handleStartChannel(channel)}
                  sx={{
                    cursor: 'pointer',
                    backgroundImage: `url(${videoPlaceholder})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    '&:hover .play-glass': {
                      transform: 'scale(1.08)',
                      backgroundColor: alpha(theme.palette.success.main, 0.25),
                      borderColor: alpha(theme.palette.success.main, 0.6),
                      boxShadow: `0 0 24px ${alpha(theme.palette.success.main, 0.3)}`,
                    },
                    '&:hover .play-icon': {
                      color: theme.palette.success.main,
                    },
                    '&:hover .play-label': {
                      opacity: 1,
                    },
                    '&:hover .idle-overlay': {
                      backgroundColor: 'rgba(0,0,0,0.35)',
                    },
                  }}
                >
                  {/* Dark overlay for contrast */}
                  <Box
                    className="idle-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      transition: 'background-color 0.3s ease',
                      zIndex: 1,
                    }}
                  />
                  {/* Channel badge */}
                  <CameraOverlay sx={{ zIndex: 2 }}>
                    <ChannelBadge
                      icon={<VideocamOffIcon />}
                      label={channelName}
                      isActive={false}
                      size="small"
                    />
                  </CameraOverlay>
                  {/* Play button - glassmorphism */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    <Box
                      className="play-glass"
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(8px)',
                        border: '1.5px solid rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                      }}
                    >
                      <PlayArrowIcon className="play-icon" sx={{ fontSize: 38, color: 'rgba(255,255,255,0.85)', transition: 'color 0.3s ease', ml: '3px' }} />
                    </Box>
                    <Typography
                      className="play-label"
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        mt: 1.5,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      Iniciar stream
                    </Typography>
                  </Box>
                </CameraCell>
              );
            })}
          </CameraGridStyled>
        )}

        {/* Channel Selector (for single view) */}
        {capabilitiesReady && (layout === 'single' || isMobile) && (
          <Paper
            sx={{
              mt: 1,
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.divider,
            }}
          >
            {Array.from({ length: channelCount }, (_, i) => i + 1).map((ch) => (
              <Chip
                key={ch}
                label={CHANNEL_NAMES[ch]}
                onClick={() => {
                  setSelectedChannel(ch);
                  if (!activeStreaming.has(ch)) {
                    handleStartChannel(ch);
                  }
                }}
                variant={selectedChannel === ch ? 'filled' : 'outlined'}
                color={activeStreaming.has(ch) ? 'success' : 'default'}
                sx={{
                  borderColor: theme.palette.divider,
                  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.1) },
                }}
              />
            ))}
          </Paper>
        )}
      </ContentArea>

      {/* Stream timer snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.warningChannel ? null : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar.message}
        action={snackbar.warningChannel ? (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => {
              // Extend ALL active channels at once
              activeStreamingRef.current.forEach((ch) => extendTimer(ch));
              setSnackbar({ open: false, message: '', warningChannel: null });
            }}
            sx={{ fontWeight: 600, borderColor: 'rgba(255,255,255,0.5)' }}
          >
            Estender todos +5min
          </Button>
        ) : undefined}
      />
    </PageContainer>
  );
};

export default CameraViewPage;
